import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Autocomplete,
  Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Tabs, Tab,
  FormControl, InputLabel, Select, FormControlLabel, Switch,
} from '@mui/material';
import {
  Add as AddIcon, MoreVert as MoreVertIcon,
  Login as CheckInIcon, CheckCircle as CompleteIcon,
  EventBusy as NoShowIcon, EventRepeat as RescheduleIcon,
  Cancel as CancelIcon, Receipt as InvoiceIcon,
  Pets as ViewPatientIcon, CalendarMonth as RebookIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Appointment {
  id: string;
  pet_id: number;
  pet_name: string;
  client_id: number;
  owner_name: string;
  deposit_required: number;
  veterinarian_name: string | null;
  appointment_type_name: string | null;
  service_name: string | null;
  appointment_date: string;
  appointment_time_start: string;
  reason_for_visit: string | null;
  status: string;
  cancellation_reason: string | null;
}

interface PatientOption { id: string; label: string; }
interface VetOption { id: string; label: string; }
interface ServiceOption { id: string; label: string; }
interface ApptTypeOption { id: string; label: string; }

const emptyForm = { date: '', time: '', timeEnd: '', reason: '' };

const STATUS_COLORS: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default' | 'primary'> = {
  Scheduled: 'info',
  Confirmed: 'primary',
  'In Progress': 'warning',
  'Checked In': 'warning',
  Completed: 'success',
  Documented: 'success',
  Cancelled: 'error',
  'No Show': 'default',
};

const CANCELLATION_REASONS = [
  { value: 'client_request', label: 'Client Request (Scheduling Conflict)' },
  { value: 'no_show', label: 'No-Show (Client never arrived)' },
  { value: 'clinic_request', label: 'Clinic Request (Vet / equipment unavailable)' },
  { value: 'pet_deceased', label: '⚠️ Pet Deceased' },
  { value: 'other', label: 'Other (requires notes)' },
];

const AppointmentsPage = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);

  // New appointment dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [patientOptions, setPatientOptions] = useState<PatientOption[]>([]);
  const [vetOptions, setVetOptions] = useState<VetOption[]>([]);
  const [serviceOptions, setServiceOptions] = useState<ServiceOption[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [selectedVet, setSelectedVet] = useState<VetOption | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(null);
  const [apptTypeOptions, setApptTypeOptions] = useState<ApptTypeOption[]>([]);
  const [selectedApptType, setSelectedApptType] = useState<ApptTypeOption | null>(null);
  const [notifyCreate, setNotifyCreate] = useState(true);

  // Row action menu
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [activeAppt, setActiveAppt] = useState<Appointment | null>(null);

  // Reschedule dialog
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [notifyReschedule, setNotifyReschedule] = useState(true);

  // Cancel dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelNotify, setCancelNotify] = useState(true);

  // Tab filter
  const [tabValue, setTabValue] = useState<'upcoming' | 'all' | 'past'>('upcoming');

  useEffect(() => { fetchAppointments(); }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.APPOINTMENTS.LIST);
      setAppointments(response.data || response || []);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefData = async () => {
    try {
      const [patientsRes, vetsRes, servicesRes, typesRes] = await Promise.all([
        api.get(API_ENDPOINTS.PATIENTS.LIST),
        api.get(API_ENDPOINTS.APPOINTMENTS.REF_VETS).catch(() => ({ data: [] })),
        api.get(API_ENDPOINTS.APPOINTMENTS.REF_SERVICES).catch(() => ({ data: [] })),
        api.get(API_ENDPOINTS.APPOINTMENTS.REF_TYPES).catch(() => ({ data: [] })),
      ]);
      const patients = (patientsRes.data || patientsRes || []);
      setPatientOptions(patients.map((p: { id: string; name: string; species: string }) => ({
        id: p.id, label: `${p.name} (${p.species})`,
      })));
      const vets = (vetsRes.data || vetsRes || []);
      setVetOptions(vets.map((v: { id: string; full_name: string }) => ({ id: v.id, label: v.full_name })));
      const services = (servicesRes.data || servicesRes || []);
      setServiceOptions(services.map((s: { id: string; name: string }) => ({ id: s.id, label: s.name })));
      const types = (typesRes.data || typesRes || []);
      setApptTypeOptions(types.map((t: { id: string; name: string }) => ({ id: t.id, label: t.name })));
    } catch (err) {
      console.error('Failed to fetch ref data:', err);
    }
  };

  // ── Row menu ──────────────────────────────────────────────────────────────
  const openMenu = (e: React.MouseEvent<HTMLElement>, appt: Appointment) => {
    setMenuAnchor(e.currentTarget);
    setActiveAppt(appt);
  };
  const closeMenu = () => { setMenuAnchor(null); };

  // ── Status transitions ────────────────────────────────────────────────────
  const changeStatus = async (appt: Appointment, newStatus: string) => {
    closeMenu();
    try {
      await api.put(API_ENDPOINTS.APPOINTMENTS.UPDATE(appt.id), { status: newStatus });
      fetchAppointments();
    } catch (err) {
      console.error('Status change failed:', err);
    }
  };

  // ── Reschedule ────────────────────────────────────────────────────────────
  const openReschedule = () => {
    if (!activeAppt) return;
    setRescheduleDate(activeAppt.appointment_date);
    setRescheduleTime(activeAppt.appointment_time_start);
    setNotifyReschedule(true);
    closeMenu();
    setRescheduleOpen(true);
  };

  const handleReschedule = async () => {
    if (!activeAppt || !rescheduleDate || !rescheduleTime) return;
    setRescheduleSaving(true);
    try {
      await api.put(API_ENDPOINTS.APPOINTMENTS.UPDATE(activeAppt.id), {
        appointment_date: rescheduleDate,
        appointment_time_start: rescheduleTime,
        status: 'Scheduled',
        notify_client: notifyReschedule,
      });
      setRescheduleOpen(false);
      fetchAppointments();
    } catch (err) {
      console.error('Reschedule failed:', err);
    } finally {
      setRescheduleSaving(false);
    }
  };

  // ── Cancel ────────────────────────────────────────────────────────────────
  const openCancel = () => {
    setCancelReason('');
    setCancelNotes('');
    setCancelNotify(true);
    closeMenu();
    setCancelOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!activeAppt || !cancelReason) return;
    if (cancelReason === 'other' && !cancelNotes.trim()) return;
    setCancelSaving(true);
    try {
      await api.post(API_ENDPOINTS.APPOINTMENTS.CANCEL(activeAppt.id), {
        cancellation_reason: cancelReason,
        cancellation_notes: cancelNotes.trim() || undefined,
        notify_client: cancelReason === 'pet_deceased' ? false : cancelNotify,
      });
      setCancelOpen(false);
      fetchAppointments();
    } catch (err) {
      console.error('Cancel failed:', err);
    } finally {
      setCancelSaving(false);
    }
  };

  // ── Rebook (open new appt dialog prefilled) ───────────────────────────────
  const handleRebook = () => {
    closeMenu();
    const today = new Date().toISOString().split('T')[0];
    setForm({ date: today, time: '09:00', timeEnd: '', reason: activeAppt?.reason_for_visit || '' });
    if (activeAppt) {
      setSelectedPatient({ id: String(activeAppt.pet_id), label: activeAppt.pet_name });
    }
    setSelectedVet(null);
    setSelectedService(null);
    setSelectedApptType(null);
    setNotifyCreate(true);
    setError(null);
    setSuccess(false);
    setDialogOpen(true);
    fetchRefData();
  };

  // ── New appointment dialog ────────────────────────────────────────────────
  const handleOpen = () => {
    setForm(emptyForm);
    setSelectedPatient(null);
    setSelectedVet(null);
    setSelectedService(null);
    setSelectedApptType(null);
    setNotifyCreate(true);
    setError(null);
    setSuccess(false);
    setDialogOpen(true);
    fetchRefData();
  };

  const handleSubmit = async () => {
    if (!selectedPatient || !form.date || !form.time) {
      setError('Patient, date and time are required.');
      return;
    }
    if (!selectedVet) {
      setError('Veterinarian is required.');
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (form.date < today) {
      setError('Appointment date cannot be in the past.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(API_ENDPOINTS.APPOINTMENTS.CREATE, {
        pet_id: selectedPatient.id,
        veterinarian_id: selectedVet?.id,
        appointment_date: form.date,
        appointment_time_start: form.time,
        appointment_time_end: form.timeEnd || undefined,
        reason_for_visit: form.reason,
        service_id: selectedService?.id || undefined,
        appointment_type_id: selectedApptType?.id || undefined,
        notify_client: notifyCreate,
      });
      setSuccess(true);
      await fetchAppointments();
      setTimeout(() => setDialogOpen(false), 800);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Failed to create appointment.');
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const menuItems = (appt: Appointment) => {
    const s = appt.status;
    const items = [];

    if (s === 'Scheduled' || s === 'Confirmed') {
      items.push(
        <MenuItem key="checkin" onClick={() => changeStatus(appt, 'In Progress')}>
          <ListItemIcon><CheckInIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>Check In</ListItemText>
        </MenuItem>,
        <MenuItem key="reschedule" onClick={openReschedule}>
          <ListItemIcon><RescheduleIcon fontSize="small" color="info" /></ListItemIcon>
          <ListItemText>Reschedule</ListItemText>
        </MenuItem>,
        <MenuItem key="cancel" onClick={openCancel}>
          <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Cancel Appointment</ListItemText>
        </MenuItem>,
      );
    }

    if (s === 'In Progress' || s === 'Checked In') {
      items.push(
        <MenuItem key="complete" onClick={() => changeStatus(appt, 'Completed')}>
          <ListItemIcon><CompleteIcon fontSize="small" color="success" /></ListItemIcon>
          <ListItemText>Mark Complete</ListItemText>
        </MenuItem>,
        <MenuItem key="noshow" onClick={() => changeStatus(appt, 'No Show')}>
          <ListItemIcon><NoShowIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Mark No Show</ListItemText>
        </MenuItem>,
      );
    }

    if (s === 'Completed') {
      items.push(
        <MenuItem key="invoice" onClick={() => { closeMenu(); navigate('/invoicing'); }}>
          <ListItemIcon><InvoiceIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>Create Invoice</ListItemText>
        </MenuItem>,
        <MenuItem key="rebook" onClick={handleRebook}>
          <ListItemIcon><RebookIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rebook</ListItemText>
        </MenuItem>,
      );
    }

    if (s === 'Cancelled' || s === 'No Show') {
      items.push(
        <MenuItem key="rebook" onClick={handleRebook}>
          <ListItemIcon><RebookIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText>Rebook</ListItemText>
        </MenuItem>,
      );
    }

    // Always: view patient
    items.push(
      <MenuItem key="viewpatient" onClick={() => { closeMenu(); navigate(`/patients/${appt.pet_id}`); }}>
        <ListItemIcon><ViewPatientIcon fontSize="small" /></ListItemIcon>
        <ListItemText>View Patient</ListItemText>
      </MenuItem>,
    );

    return items;
  };

  const today = new Date().toISOString().split('T')[0];

  const filteredAppointments = appointments.filter(appt => {
    if (tabValue === 'upcoming') return appt.appointment_date >= today;
    if (tabValue === 'past') return appt.appointment_date < today;
    return true;
  });

  const isPetDeceased = cancelReason === 'pet_deceased';
  const cancelValid = !!cancelReason && (cancelReason !== 'other' || cancelNotes.trim().length > 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Appointments</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
          New Appointment
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Upcoming (${appointments.filter(a => a.appointment_date >= today).length})`} value="upcoming" />
        <Tab label={`All (${appointments.length})`} value="all" />
        <Tab label={`Past (${appointments.filter(a => a.appointment_date < today).length})`} value="past" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Type / Service</TableCell>
              <TableCell>Veterinarian</TableCell>
              <TableCell>Date & Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAppointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No appointments found</TableCell>
              </TableRow>
            ) : filteredAppointments.map((appt) => (
              <TableRow key={appt.id} hover>
                <TableCell>{appt.pet_name}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    {appt.owner_name}
                    {appt.deposit_required === 1 && (
                      <Chip label="Deposit Required" color="warning" size="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{appt.appointment_type_name || appt.service_name || '—'}</TableCell>
                <TableCell>{appt.veterinarian_name || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {appt.appointment_date} {appt.appointment_time_start}
                </TableCell>
                <TableCell>
                  <Chip
                    label={appt.status}
                    color={STATUS_COLORS[appt.status] ?? 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Actions">
                    <IconButton size="small" onClick={(e) => openMenu(e, appt)}>
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Row action menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {activeAppt ? menuItems(activeAppt) : null}
      </Menu>

      {/* Reschedule dialog */}
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          {activeAppt && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Current: <strong>{activeAppt.appointment_date}</strong> at <strong>{activeAppt.appointment_time_start}</strong>
            </Typography>
          )}
          <Box display="flex" gap={2} mt={1}>
            <TextField label="New Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
              value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} />
            <TextField label="New Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
              value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} />
          </Box>
          <Box mt={2}>
            <FormControlLabel
              control={<Switch checked={notifyReschedule} onChange={e => setNotifyReschedule(e.target.checked)} />}
              label="Notify client of updated appointment time"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)} disabled={rescheduleSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleReschedule} disabled={rescheduleSaving || !rescheduleDate || !rescheduleTime}>
            {rescheduleSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          {activeAppt && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              <strong>{activeAppt.pet_name}</strong> ({activeAppt.owner_name}) on{' '}
              <strong>{activeAppt.appointment_date} {activeAppt.appointment_time_start}</strong>
            </Typography>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth required>
              <InputLabel>Cancellation Reason *</InputLabel>
              <Select
                value={cancelReason}
                label="Cancellation Reason *"
                onChange={e => {
                  setCancelReason(e.target.value);
                  if (e.target.value === 'pet_deceased') setCancelNotify(false);
                  else setCancelNotify(true);
                }}
              >
                {CANCELLATION_REASONS.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {isPetDeceased && (
              <Alert severity="warning" icon={<WarningIcon />}>
                This will mark <strong>{activeAppt?.pet_name}</strong> as deceased across the entire CRM and halt all future communications.
              </Alert>
            )}

            <TextField
              label={cancelReason === 'other' ? 'Internal Notes *' : 'Internal Notes (optional)'}
              multiline
              rows={3}
              fullWidth
              placeholder="e.g. Owner called — car broke down."
              value={cancelNotes}
              onChange={e => setCancelNotes(e.target.value)}
              required={cancelReason === 'other'}
            />

            <Tooltip
              title={isPetDeceased ? 'Notification suppressed for deceased pet' : ''}
              placement="top-start"
            >
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!isPetDeceased && cancelNotify}
                      onChange={e => setCancelNotify(e.target.checked)}
                      disabled={isPetDeceased}
                    />
                  }
                  label="Send cancellation confirmation to client"
                />
              </span>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={cancelSaving}>Keep</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelConfirm}
            disabled={cancelSaving || !cancelValid}
          >
            {cancelSaving ? <CircularProgress size={20} /> : 'Cancel Appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Appointment dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Appointment</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>Appointment created successfully.</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Autocomplete options={patientOptions} getOptionLabel={o => o.label}
              value={selectedPatient} onChange={(_, val) => setSelectedPatient(val)}
              renderInput={params => <TextField {...params} label="Patient *" />} />
            <Autocomplete options={vetOptions} getOptionLabel={o => o.label}
              value={selectedVet} onChange={(_, val) => setSelectedVet(val)}
              renderInput={params => <TextField {...params} label="Veterinarian *" />} />
            <Autocomplete options={serviceOptions} getOptionLabel={o => o.label}
              value={selectedService} onChange={(_, val) => setSelectedService(val)}
              renderInput={params => <TextField {...params} label="Service / Type" />} />
            <Autocomplete options={apptTypeOptions} getOptionLabel={o => o.label}
              value={selectedApptType} onChange={(_, val) => setSelectedApptType(val)}
              renderInput={params => <TextField {...params} label="Appointment Type" />} />
            <Box display="flex" gap={2}>
              <TextField label="Date *" type="date" fullWidth InputLabelProps={{ shrink: true }}
                value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                inputProps={{ min: new Date().toISOString().slice(0, 10) }} />
              <TextField label="Time *" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} />
              <TextField label="End Time" type="time" fullWidth InputLabelProps={{ shrink: true }}
                value={form.timeEnd} onChange={e => setForm(f => ({ ...f, timeEnd: e.target.value }))} />
            </Box>
            <TextField label="Reason for Visit" multiline rows={3} fullWidth
              value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            <FormControlLabel
              control={<Switch checked={notifyCreate} onChange={e => setNotifyCreate(e.target.checked)} />}
              label="Send booking confirmation to client"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Book Appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentsPage;
